import {
  getFormatDateFromObject,
  getDateFromString,
  getCourseScholarships,
  calculateFeeAfterScholarships,
  parseDate,
  formatDate,
  isIntakeAfterAgentDetailsCutoff,
} from 'shared-components';
import {
  AGENT_DETAIL_FIELD_LABELS,
  AgentDetailFieldName,
  AgentDisplayNameConflictData,
  IConflictFactors,
  IImportOffer,
  InfoField,
  InfoOption,
  InfoState,
  ISystemAndUniConfig,
  PassportDoc,
  IImportCourse,
  SectionStatusType,
  TransactionStatus,
  ConflictData,
  CourseLevelOfferStatus,
  IOffer,
} from 'types';
import { StudentState } from '../../../../store/student/types';
import { boolToYesNo, formatCurrency } from '../../../../utilities/formatting';
import { getVisible, hasDocumentType } from '../../../../utilities/documents';
import { titleCase, getImportOffer } from '../../../../utilities/helpers';

const IMPORT_LABEL = 'Import Value';
const STUDENT_LABEL = 'Updated Value';
const MANUAL_LABEL = 'Staff Entered Value';

const PASSPORT_LABEL = 'Passport';

function getProcessedValue(value: string | number | undefined): string | number {
  if (value && typeof value === 'string') {
    return value.toLowerCase().trim();
  }
  if (value !== undefined) {
    return value;
  }
  return '';
}

function determineConflict(conflictFactors: IConflictFactors): InfoState {
  const {
    hasStudentValue,
    hasImportValue,
    hasPassportValue,
    studentValue,
    importValue,
    passportValue,
    conflictHistory,
    allowEmptyValue = false,
  } = conflictFactors;

  let state;

  if (hasStudentValue) {
    state = InfoState.Filled;
  } else if (hasPassportValue) {
    state = InfoState.NotFilledPassport;
  } else {
    state = hasImportValue ? InfoState.NotFilledImport : InfoState.NotFilled;
  }

  const sluggedConflictImportValue = getProcessedValue(conflictHistory?.import);
  const sluggedConflictPassportValue = getProcessedValue(conflictHistory?.passport);
  const sluggedResolvedValue = getProcessedValue(conflictHistory?.resolvedValue);
  const sluggedImportValue = getProcessedValue(importValue);
  const sluggedStudentValue = getProcessedValue(studentValue);
  const sluggedPassportValue = getProcessedValue(passportValue);

  if (hasStudentValue && sluggedImportValue === sluggedStudentValue) {
    // student has chosen to approve import filled value
    state = InfoState.Approved;
  }

  if (hasStudentValue && sluggedStudentValue === sluggedResolvedValue) {
    // user has chosen to resolve conflict with import, student or passport value
    state = InfoState.Resolved;
  }

  // does the student have a conflict?
  const hasConflictHistory = !!conflictHistory;
  const isResolvedValueEmpty = sluggedResolvedValue === '';
  const isStudentValueResolved = sluggedStudentValue === sluggedResolvedValue;
  const hasImportValueBeenUpdated = sluggedConflictImportValue !== sluggedImportValue;
  const hasPassportValueBeenUpdated = sluggedConflictPassportValue !== sluggedPassportValue;
  const hasImportValueBeenSet = sluggedImportValue === sluggedStudentValue;
  const hasPassportValueBeenSet = sluggedPassportValue === sluggedStudentValue;

  // Do not show conflicts, if no student value.
  if (hasStudentValue) {
    if (hasConflictHistory) {
      // has conflict history
      if (
        (!allowEmptyValue && isResolvedValueEmpty) ||
        !isStudentValueResolved ||
        (hasImportValueBeenUpdated && sluggedImportValue !== sluggedResolvedValue) ||
        (hasPassportValueBeenUpdated && sluggedPassportValue !== sluggedResolvedValue)
      ) {
        // the conflict has not been resolved yet OR
        // the import or passport value has been updated since the conlict was raised
        state = InfoState.Conflict;
      }
    } else {
      // there's no conflict history and the value has been set in the student record
      if ((hasImportValue && !hasImportValueBeenSet) || (hasPassportValue && !hasPassportValueBeenSet)) {
        // the value in the student record doesn't match the value in import or passport
        state = InfoState.Conflict;
      }
    }
  }
  return state;
}

function studentNo(student: StudentState): InfoField {
  const importValue = student.importData.studentNo ?? '';
  const studentValue = student.personalInfo.studentNo ?? '';
  const conflictHistory = student.conflictHistory.studentNo;
  const hasImportValue = !!importValue;
  const hasStudentValue = !!studentValue;
  const options: InfoOption[] = [
    {
      id: 'import',
      label: IMPORT_LABEL,
      value: importValue,
      conflictHistory: conflictHistory?.import,
    },
    {
      id: 'student',
      label: STUDENT_LABEL,
      value: studentValue,
      conflictHistory: conflictHistory?.student,
      disabled: true,
    },
  ];

  const state = determineConflict({
    hasStudentValue,
    hasImportValue,
    studentValue,
    importValue,
    conflictHistory,
  });

  return {
    id: 'studentNo',
    label: 'Student Number',
    values: {
      import: importValue,
      student: studentValue,
    },
    options,
    state,
    editPage: 'personal-info/step-5',
  };
}

function uniPartnerId(student: StudentState): InfoField {
  const studentValue = student.core.uniPartnerId ?? '';
  const hasStudentValue = !!studentValue;
  const options: InfoOption[] = [];

  // uni id can not be changed by student, so its always approved
  const state = hasStudentValue ? InfoState.Approved : InfoState.Optional;

  return {
    id: 'uniPartnerId',
    label: 'University Student Number',
    values: { student: studentValue },
    options,
    state,
    ...(hasStudentValue ? { editPage: 'edit-uni-partner-id-modal' } : {}),
    ...(!hasStudentValue ? { addFieldBtn: { label: 'Add (optional)', action: 'edit-uni-partner-id-modal' } } : {}),
  };
}

function gender(student: StudentState): InfoField {
  const importValue = titleCase(student.importData.gender ?? '');
  const studentValue = titleCase(student.personalInfo.gender ?? '');
  const passportValue = titleCase(student.mrzData?.gender ?? '');
  const conflictHistory = student.conflictHistory.gender as ConflictData<string>;
  const hasImportValue = !!importValue;
  const hasStudentValue = !!studentValue;
  const hasPassportValue = !!passportValue;
  const options: InfoOption[] = [
    {
      id: 'import',
      label: IMPORT_LABEL,
      value: importValue,
      conflictHistory: titleCase(conflictHistory?.import ?? ''),
    },
    {
      id: 'student',
      label: STUDENT_LABEL,
      value: studentValue,
      conflictHistory: titleCase(conflictHistory?.student ?? ''),
    },
    ...(hasPassportValue
      ? [
          {
            id: 'passport',
            label: PASSPORT_LABEL,
            value: passportValue,
            conflictHistory: titleCase(conflictHistory?.passport ?? ''),
          },
        ]
      : []),
  ];

  const state = determineConflict({
    hasStudentValue,
    hasImportValue,
    hasPassportValue,
    studentValue,
    importValue,
    passportValue,
    conflictHistory,
  });

  return {
    id: 'gender',
    label: 'Gender',
    values: {
      import: importValue,
      student: studentValue,
      passport: passportValue,
    },
    options,
    state,
    editPage: 'personal-info/step-2',
  };
}

function familyName(student: StudentState): InfoField {
  const importValue = titleCase(student.importData.familyName ?? '');
  const studentValue = titleCase(student.personalInfo.surname ?? '');
  const passportValue = titleCase(student.mrzData?.familyName ?? '');
  const conflictHistory = student.conflictHistory.familyName as ConflictData<string>;
  const hasImportValue = !!importValue;
  const hasStudentValue = !!studentValue;
  const hasPassportValue = !!passportValue;
  const options: InfoOption[] = [
    {
      id: 'import',
      label: IMPORT_LABEL,
      value: importValue,
      conflictHistory: titleCase(conflictHistory?.import ?? ''),
    },
    {
      id: 'student',
      label: STUDENT_LABEL,
      value: studentValue,
      conflictHistory: titleCase(conflictHistory?.student ?? ''),
    },
    ...(hasPassportValue
      ? [
          {
            id: 'passport',
            label: PASSPORT_LABEL,
            value: passportValue,
            conflictHistory: titleCase(conflictHistory?.passport ?? ''),
          },
        ]
      : []),
  ];

  const state = determineConflict({
    hasStudentValue,
    hasImportValue,
    hasPassportValue,
    studentValue,
    importValue,
    passportValue,
    conflictHistory,
  });

  return {
    id: 'familyName',
    label: 'Family Name',
    values: {
      import: importValue,
      student: studentValue,
      passport: passportValue,
    },
    options,
    state,
    editPage: 'personal-info/step-1',
  };
}

function middleName(student: StudentState): InfoField {
  const importValue = student.importData.otherName ?? '';
  const studentValue = student.personalInfo.middleName ?? '';
  const conflictHistory = student.conflictHistory.middleName;
  // middle name conflict can be resolved with empty value
  const hasImportValue = !!(importValue || conflictHistory);
  // middle name may be empty - allow this if page with this question is complete
  const hasStudentValue = !!(studentValue || student.offer.isPIPage1Complete);
  const options: InfoOption[] = [
    {
      id: 'import',
      label: IMPORT_LABEL,
      value: importValue,
      conflictHistory: conflictHistory?.import,
    },
    {
      id: 'student',
      label: STUDENT_LABEL,
      value: studentValue,
      conflictHistory: conflictHistory?.student,
    },
  ];

  const state = determineConflict({
    hasStudentValue,
    hasImportValue,
    studentValue,
    importValue,
    conflictHistory,
    allowEmptyValue: true,
  });

  return {
    id: 'middleName',
    label: 'Middle Name',
    values: {
      import: importValue,
      student: studentValue,
    },
    options,
    state,
    editPage: 'personal-info/step-1',
  };
}

function givenName(student: StudentState): InfoField {
  const importValue = titleCase(student.importData.givenName ?? '');
  const studentValue = titleCase(student.personalInfo.givenName ?? '');
  const passportValue = titleCase(student.mrzData?.givenName ?? '');
  const conflictHistory = student.conflictHistory.givenName as ConflictData<string>;
  const hasImportValue = !!importValue;
  const hasStudentValue = !!studentValue;
  const hasPassportValue = !!passportValue;
  const options: InfoOption[] = [
    {
      id: 'import',
      label: IMPORT_LABEL,
      value: importValue,
      conflictHistory: titleCase(conflictHistory?.import ?? ''),
    },
    {
      id: 'student',
      label: STUDENT_LABEL,
      value: studentValue,
      conflictHistory: titleCase(conflictHistory?.student ?? ''),
    },
    ...(hasPassportValue
      ? [
          {
            id: 'passport',
            label: PASSPORT_LABEL,
            value: passportValue,
            conflictHistory: titleCase(conflictHistory?.passport ?? ''),
          },
        ]
      : []),
  ];

  // CS-2027 don't raise conflict on middle name for OCR
  const passportValueHasMiddleName = passportValue.split(' ').length > 1;
  let middleNameStudentValue = '';

  if (hasPassportValue && passportValueHasMiddleName) {
    const importedMiddleName = student.importData.otherName ?? '';
    const studentMiddleNameValue = student.personalInfo.middleName ?? '';
    const resolvedMiddleNameValue = student.conflictHistory.middleName?.resolvedValue ?? '';
    if (resolvedMiddleNameValue !== '') {
      middleNameStudentValue = resolvedMiddleNameValue;
    } else if (studentMiddleNameValue !== '') {
      middleNameStudentValue = studentMiddleNameValue;
    } else if (importedMiddleName !== '') {
      middleNameStudentValue = importedMiddleName;
    }
    if (
      studentValue.toLowerCase().includes(middleNameStudentValue.toLowerCase()) ||
      (resolvedMiddleNameValue !== '' && resolvedMiddleNameValue !== importedMiddleName)
    ) {
      // special case where student has middle name, but has also put it in their first name, ALSO
      // don't compare middleName if it's been resolved and doesn't match the import value
      middleNameStudentValue = '';
    }
  }

  const hasBeenAmendedPostResolve =
    !!conflictHistory?.resolvedValue && studentValue.toLowerCase() !== conflictHistory?.resolvedValue.toLowerCase();

  const finalStudentValue =
    conflictHistory?.resolvedValue && !hasBeenAmendedPostResolve
      ? conflictHistory.resolvedValue
      : `${studentValue} ${middleNameStudentValue}`;
  const finalImportValue =
    conflictHistory?.resolvedValue && !hasBeenAmendedPostResolve
      ? conflictHistory.resolvedValue
      : `${importValue} ${middleNameStudentValue}`;
  const finalPassportValue =
    conflictHistory?.resolvedValue && !hasBeenAmendedPostResolve ? conflictHistory.resolvedValue : passportValue;

  const state = determineConflict({
    hasStudentValue,
    hasImportValue,
    hasPassportValue,
    studentValue: finalStudentValue,
    importValue: finalImportValue,
    passportValue: finalPassportValue,
    conflictHistory,
  });

  return {
    id: 'givenName',
    label: 'Given Names',
    values: {
      import: importValue,
      student: studentValue,
      passport: passportValue,
    },
    options,
    state,
    editPage: 'personal-info/step-1',
  };
}

function email(student: StudentState): InfoField {
  const importValue = student.importData.email ?? '';
  const studentValue = student.core.email ?? '';
  const hasImportValue = !!importValue;
  const options: InfoOption[] = [];

  // email can not be changed and is always present
  const state = hasImportValue ? InfoState.Approved : InfoState.Filled;

  return {
    id: 'email',
    label: 'Email Address',
    values: {
      import: importValue,
      student: studentValue,
    },
    options,
    state,
  };
}

function dob(student: StudentState): InfoField {
  const importValue = getFormatDateFromObject(getDateFromString(student.importData.dob ?? ''));
  const studentValue = getFormatDateFromObject(getDateFromString(student.personalInfo.dob ?? ''));
  const passportValue = getFormatDateFromObject(getDateFromString(student.mrzData?.dob ?? ''));
  const conflictHistory = student.conflictHistory.dob as ConflictData<string>;
  const hasImportValue = !!importValue;
  const hasStudentValue = !!studentValue;
  const hasPassportValue = !!passportValue;
  const options: InfoOption[] = [
    {
      id: 'import',
      label: IMPORT_LABEL,
      value: importValue,
      conflictHistory: getFormatDateFromObject(getDateFromString(conflictHistory?.import)),
    },
    {
      id: 'student',
      label: STUDENT_LABEL,
      value: studentValue,
      conflictHistory: getFormatDateFromObject(getDateFromString(conflictHistory?.student)),
    },
    ...(hasPassportValue
      ? [
          {
            id: 'passport',
            label: PASSPORT_LABEL,
            value: passportValue,
            conflictHistory: getFormatDateFromObject(getDateFromString(conflictHistory?.passport)),
          },
        ]
      : []),
  ];

  const state = determineConflict({
    hasStudentValue,
    hasImportValue,
    hasPassportValue,
    studentValue: student.personalInfo.dob ?? '',
    importValue: student.importData.dob ?? '',
    passportValue: student.mrzData?.dob ?? '',
    conflictHistory,
  });

  return {
    id: 'dob',
    label: 'Date of Birth',
    values: {
      import: importValue || '<no data provided>',
      student: studentValue,
      passport: passportValue,
    },
    options,
    state,
    editPage: 'personal-info/step-2',
  };
}

function nationality(student: StudentState): InfoField {
  const importValue = titleCase(student.importData.nationality ?? '');
  const studentValue = titleCase(student.personalInfo.nationality ?? '');
  const passportValue = titleCase(student.mrzData?.nationality ?? '');
  const conflictHistory = student.conflictHistory.nationality as ConflictData<string>;
  const hasImportValue = !!importValue;
  const hasStudentValue = !!studentValue;
  const hasPassportValue = !!passportValue;
  const options: InfoOption[] = [
    {
      id: 'import',
      label: IMPORT_LABEL,
      value: importValue,
      conflictHistory: conflictHistory?.import,
    },
    {
      id: 'student',
      label: STUDENT_LABEL,
      value: studentValue,
      conflictHistory: conflictHistory?.student,
    },
    ...(hasPassportValue
      ? [
          {
            id: 'passport',
            label: PASSPORT_LABEL,
            value: passportValue,
            conflictHistory: conflictHistory?.passport,
          },
        ]
      : []),
  ];

  const state = determineConflict({
    hasStudentValue,
    hasImportValue,
    hasPassportValue,
    studentValue,
    importValue,
    passportValue,
    conflictHistory,
  });

  return {
    id: 'nationality',
    label: 'Nationality',
    subLabel: '(passport being used)',
    values: {
      import: importValue,
      student: studentValue,
      passport: passportValue,
    },
    options,
    state,
    editPage: 'personal-info/step-2',
  };
}

function birthCountry(student: StudentState): InfoField {
  const importValue = student.importData.countryOfBirth ?? '';
  const studentValue = student.personalInfo.birthCountry ?? '';
  const conflictHistory = student.conflictHistory.birthCountry;
  const hasImportValue = !!importValue;
  const hasStudentValue = !!studentValue;
  const options: InfoOption[] = [
    {
      id: 'import',
      label: IMPORT_LABEL,
      value: importValue,
      conflictHistory: conflictHistory?.import,
    },
    {
      id: 'student',
      label: STUDENT_LABEL,
      value: studentValue,
      conflictHistory: conflictHistory?.student,
    },
  ];

  const state = determineConflict({
    hasStudentValue,
    hasImportValue,
    studentValue,
    importValue,
    conflictHistory,
  });

  return {
    id: 'birthCountry',
    label: 'Country of Birth',
    values: {
      import: importValue,
      student: studentValue,
    },
    options,
    state,
    editPage: 'personal-info/step-2',
  };
}

function getStudentPassportDisplayValue(
  studentHasPassport: boolean,
  studentPendingPassport: boolean,
  studentValue: string,
  passportExpectedDate: string,
): string {
  if (studentHasPassport) return studentValue;
  if (studentPendingPassport) return `PENDING ${passportExpectedDate || '<missing date>'}`;
  return '';
}

function getStudentPassportPlainValue(
  studentHasPassport: boolean,
  studentPendingPassport: boolean,
  studentValue: string,
  passportExpectedDate: string,
): string {
  if (studentHasPassport) return studentValue;
  if (studentPendingPassport) return passportExpectedDate || '<missing date>';
  return '';
}

function passportNumber(student: StudentState): InfoField {
  const importValue = student.importData.passportNumber ?? '';
  const studentValue = student.personalInfo.passportNumber ?? '';
  const passportValue = student.mrzData?.docNumber ?? '';
  const conflictHistory = student.conflictHistory.passportNumber as ConflictData<string>;
  const hasStudentValue = !!studentValue;
  const hasImportValue = !!importValue;
  const hasPassportValue = !!passportValue;
  const { passportStatus, passportExpectedDate, passportExpiryDate } = student.personalInfo;
  const studentHasPassport = passportStatus === 'Yes';
  const studentPendingPassport = ['Applied', 'GoingToApply'].includes(passportStatus ?? '');

  let state;
  if (hasImportValue) {
    if (studentHasPassport) {
      state = determineConflict({
        hasStudentValue,
        hasImportValue,
        hasPassportValue,
        studentValue,
        importValue,
        passportValue,
        conflictHistory,
      });
    } else if (studentPendingPassport) {
      state = InfoState.Conflict;
    } else {
      state = InfoState.NotFilledImport;
    }
  } else {
    if (studentHasPassport) {
      state = InfoState.Filled;
      if (hasPassportValue) {
        state = determineConflict({
          hasStudentValue,
          hasImportValue,
          hasPassportValue,
          studentValue,
          importValue,
          passportValue,
          conflictHistory,
        });
      }
    } else if (studentPendingPassport) {
      state = InfoState.Pending;
    } else {
      state = InfoState.NotFilled;
    }
  }

  let label;
  if (studentHasPassport) {
    label = `Passport Number - Exp ${
      passportExpiryDate ? formatDate(parseDate(passportExpiryDate), 'DD/MM/YYYY') : '<missing date>'
    }`;
  } else {
    label = 'Passport Number';
  }
  const options: InfoOption[] = [
    {
      id: 'import',
      label: IMPORT_LABEL,
      value: importValue,
      conflictHistory: conflictHistory?.import,
      disabled: studentPendingPassport,
    },
    {
      id: 'student',
      label: STUDENT_LABEL,
      value: getStudentPassportDisplayValue(
        studentHasPassport,
        studentPendingPassport,
        studentValue,
        passportExpectedDate,
      ),
      conflictHistory: conflictHistory?.student,
      valuePending: studentPendingPassport,
      disabled: studentPendingPassport,
    },
    ...(hasPassportValue
      ? [
          {
            id: 'passport',
            label: PASSPORT_LABEL,
            value: passportValue,
            conflictHistory: conflictHistory?.passport,
          },
        ]
      : []),
  ];

  return {
    id: 'passportNumber',
    label,
    values: {
      import: importValue,
      student: getStudentPassportPlainValue(
        studentHasPassport,
        studentPendingPassport,
        studentValue,
        passportExpectedDate,
      ),
      passport: passportValue,
    },
    options,
    state,
    editPage: 'personal-info/step-7',
  };
}

function passportNumberRenew(student: StudentState): InfoField {
  const renewDocs = getVisible({ documentsHash: student.documents.passport.renew ?? {} });
  // based on data in last passport (renew) doc
  const doc: PassportDoc | undefined = renewDocs[renewDocs.length - 1]?.item;

  let state;
  let docNumber: string;

  if (doc) {
    if (['UPLOADED_OK', 'APPROVED', 'ACKNOWLEDGED'].includes(doc?.status)) {
      const isInfoOnlyDoc = hasDocumentType(doc, 'infoOnly');
      state = InfoState.Filled;
      docNumber = isInfoOnlyDoc ? '<not provided>' : (doc.docNumber ?? '');
    } else {
      docNumber = '';
      state = InfoState.Pending;
    }
  } else {
    docNumber = '';
    state = InfoState.Pending;
  }

  return {
    id: 'passportNumberRenew',
    label: 'Passport Number - Renewal',
    values: {
      student: docNumber,
    },
    options: [],
    state,
  };
}

function courseName(student: StudentState, index: number): InfoField {
  const isRemoved = isCourseRemoved(student.offer, index);

  const importOffer = getImportOffer(student.importData, student.offer.intakeId, student.offer.offerId);
  const importValue = importOffer?.courses?.[index]?.title ?? '';
  const studentValue = student.offer.courses?.[index]?.courseName ?? '';
  const conflictHistory = (student.conflictHistory.courseName ?? []).find(c => c.index === index)?.data;
  const hasStudentValue = !!studentValue;
  const hasImportValue = !!importValue;
  const options: InfoOption[] = [
    {
      id: 'import',
      label: IMPORT_LABEL,
      value: importValue || '<no data provided>',
      conflictHistory: conflictHistory?.import || '<no data provided>',
      disabled: !importValue,
    },
    {
      id: 'student',
      label: STUDENT_LABEL,
      value: studentValue,
      conflictHistory: conflictHistory?.student,
      editPage: 'personal-info/step-5',
    },
  ];

  let state = determineConflict({
    hasStudentValue,
    hasImportValue,
    studentValue,
    importValue,
    conflictHistory,
  });

  if (isRemoved) {
    state = InfoState.Removed;
  }

  return {
    id: 'courseName',
    index,
    label: 'Course Offered',
    values: {
      import: importValue || '<no data provided>',
      student: studentValue,
    },
    options,
    state,
  };
}

function courseStartDate(student: StudentState, index: number): InfoField {
  const isRemoved = isCourseRemoved(student.offer, index);

  const importOffer = getImportOffer(student.importData, student.offer.intakeId, student.offer.offerId);
  const importValue = importOffer?.courses?.[index]?.startDate ?? '';
  const studentValue = student.offer.courses?.[index]?.startDate ?? '';
  const conflictHistory = (student.conflictHistory.courseStartDate ?? []).find(c => c.index === index)?.data;
  const hasStudentValue = !!studentValue;
  const hasImportValue = !!importValue;
  const options: InfoOption[] = [
    {
      id: 'import',
      label: IMPORT_LABEL,
      value: importValue || '<no data provided>',
      conflictHistory: conflictHistory?.import || '<no data provided>',
      disabled: !importValue,
    },
    {
      id: 'student',
      label: STUDENT_LABEL,
      value: studentValue,
      conflictHistory: conflictHistory?.student,
      editPage: 'personal-info/step-5',
    },
  ];

  let state = determineConflict({
    hasStudentValue,
    hasImportValue,
    studentValue,
    importValue,
    conflictHistory,
  });
  if (isRemoved) {
    state = InfoState.Removed;
  }

  return {
    id: 'courseStartDate',
    index,
    label: 'Course Start Date',
    values: {
      import: getFormatDateFromObject(getDateFromString(importValue)) || '<no data provided>',
      student: getFormatDateFromObject(getDateFromString(studentValue)),
    },
    options,
    state,
  };
}

function courseEndDate(student: StudentState, index: number): InfoField {
  const isRemoved = isCourseRemoved(student.offer, index);

  const importOffer = getImportOffer(student.importData, student.offer.intakeId, student.offer.offerId);
  const importValue = importOffer?.courses?.[index]?.endDate ?? '';
  const studentValue = student.offer.courses?.[index]?.endDate ?? '';
  const conflictHistory = (student.conflictHistory.courseEndDate ?? []).find(c => c.index === index)?.data;
  const hasStudentValue = !!studentValue;
  const hasImportValue = !!importValue;
  const options: InfoOption[] = [
    {
      id: 'import',
      label: IMPORT_LABEL,
      value: importValue || '<no data provided>',
      conflictHistory: conflictHistory?.import || '<no data provided>',
      disabled: !importValue,
    },
    {
      id: 'student',
      label: STUDENT_LABEL,
      value: studentValue,
      conflictHistory: conflictHistory?.student,
      editPage: 'personal-info/step-5',
    },
  ];

  let state = determineConflict({
    hasStudentValue,
    hasImportValue,
    studentValue,
    importValue,
    conflictHistory,
  });

  if (isRemoved) {
    state = InfoState.Removed;
  }

  return {
    id: 'courseEndDate',
    index,
    label: 'Course End Date',
    values: {
      import: getFormatDateFromObject(getDateFromString(importValue)) || '<no data provided>',
      student: getFormatDateFromObject(getDateFromString(studentValue)),
    },
    options,
    state,
  };
}

function courseScholarships(student: StudentState, index: number): InfoField[] {
  const result: InfoField[] = [];
  const isRemoved = isCourseRemoved(student.offer, index);

  const importOffer = getImportOffer(student.importData, student.offer.intakeId, student.offer.offerId);
  const studentCourses = student.offer.courses;
  const scholarships =
    studentCourses &&
    getCourseScholarships(
      studentCourses[index]?.courseName,
      studentCourses[index]?.courseFee,
      student.financials?.scholarshipSponsorship,
    );
  // 0 and undefined have different meanings for course fee
  // do not default values to 0
  const importValue = importOffer?.courses?.[index]?.courseScholarshipAmount1;
  let studentValue = scholarships && scholarships[`courseScholarshipAmount1_course${index}`]?.courseFundAmount;
  const conflictHistory = (student.conflictHistory.courseScholarshipAmount1 ?? []).find(c => c.index === index)?.data;
  let hasStudentValue = studentValue !== undefined;
  const hasImportValue = importValue !== undefined;
  const options: InfoOption[] = [
    {
      id: 'import',
      label: IMPORT_LABEL,
      value: importValue !== undefined ? `£${formatCurrency(importValue ?? 0)}` : '<no data provided>',
      conflictHistory:
        conflictHistory?.import !== undefined
          ? `£${formatCurrency(conflictHistory.import ?? 0)}`
          : '<no data provided>',
      disabled: importValue === undefined,
    },
    {
      id: 'student',
      label: STUDENT_LABEL,
      value: `£${formatCurrency(studentValue ?? 0)}`,
      conflictHistory: `£${formatCurrency(conflictHistory?.student ?? 0)}`,
      editPage: 'finances/step-1',
    },
  ];

  if (studentCourses?.length !== 0 && student.offer.isPIPage5Complete && hasImportValue && !hasStudentValue) {
    // ENR-549 case 1  when no declared scholarship but user reimport an scholarship later
    studentValue = 0;
    hasStudentValue = true;
  }

  let state = determineConflict({
    hasStudentValue,
    hasImportValue,
    studentValue: studentValue ?? '',
    importValue: importValue ?? '',
    conflictHistory,
  });
  if (isRemoved) {
    state = InfoState.Removed;
  }

  if (!(importValue === undefined && studentValue === undefined))
    result.push({
      id: 'courseScholarshipAmount1',
      index,
      label:
        (scholarships && scholarships[`courseScholarshipAmount1_course${index}`]?.scholarshipName) || 'Scholarship',
      values: {
        import: importValue !== undefined ? `£${formatCurrency(importValue ?? 0)}` : '<no data provided>',
        student: `£${formatCurrency(studentValue ?? 0)}`,
      },
      options,
      state,
    });

  const remainingKeys = Object.keys(scholarships ?? {}).filter(
    key => key !== `courseScholarshipAmount1_course${index}`,
  );
  for (const key of remainingKeys) {
    let state = InfoState.Filled;
    if (isRemoved) {
      state = InfoState.Removed;
    }
    const options: InfoOption[] = [];
    const scholarship = scholarships?.[key];

    result.push({
      id: key,
      label: scholarship?.scholarshipName ?? '',
      values: {
        student: `£${formatCurrency(scholarship?.courseFundAmount ?? 0)}`,
      },
      options,
      state,
    });
  }
  return result;
}

function courseFeeAfterScholarship(student: StudentState, index: number): InfoField[] {
  const result: InfoField[] = [];
  const isRemoved = isCourseRemoved(student.offer, index);
  const importOffer = getImportOffer(student.importData, student.offer.intakeId, student.offer.offerId);
  const studentCourses = student.offer.courses;
  const scholarships =
    studentCourses &&
    getCourseScholarships(
      studentCourses[index]?.courseName,
      studentCourses[index]?.courseFee,
      student.financials?.scholarshipSponsorship,
    );

  let state = InfoState.Approved;

  if (isRemoved) {
    state = InfoState.Removed;
  }

  const importScholarship = importOffer?.courses?.[index]?.courseScholarshipAmount1;
  const scholarship1Value = scholarships && scholarships[`courseScholarshipAmount1_course${index}`]?.courseFundAmount;
  const hasScholarship1Value = scholarship1Value !== undefined;
  const hasImportScholarship = importScholarship !== undefined;

  const studentValue = `£${formatCurrency(
    calculateFeeAfterScholarships(studentCourses && studentCourses[index]?.courseFee, scholarships) ?? 0,
  )}`;

  const importCourseFee = importOffer?.courses[index]?.courseFeeFirstYear ?? 0;
  const importValue = `£${formatCurrency(importCourseFee - (importScholarship ?? 0))}`;

  const options: InfoOption[] = [];
  if (!student.offer.isPIPage5Complete && hasImportScholarship) {
    state = InfoState.NotFilledImport;
  }

  if (hasScholarship1Value || hasImportScholarship || Object.keys(scholarships ?? {}).length !== 0)
    result.push({
      id: 'courseFeeAfterScholarship',
      label: 'Fee After the Discount ',
      values: {
        student: studentValue,
        import: importValue,
      },
      options,
      state,
    });
  return result;
}

function courseFee(student: StudentState, index: number): InfoField {
  const isRemoved = isCourseRemoved(student.offer, index);

  const importOffer = getImportOffer(student.importData, student.offer.intakeId, student.offer.offerId);
  // 0 and undefined have different meanings for course fee
  // do not default values to 0
  const importValue = importOffer?.courses?.[index]?.courseFeeFirstYear;
  const studentValue = student.offer.courses?.[index]?.courseFee;
  const conflictHistory = (student.conflictHistory.courseFee ?? []).find(c => c.index === index)?.data;
  const hasStudentValue = studentValue !== undefined;
  const hasImportValue = importValue !== undefined;
  const options: InfoOption[] = [
    {
      id: 'import',
      label: IMPORT_LABEL,
      value: importValue !== undefined ? `£${formatCurrency(importValue ?? 0)}` : '<no data provided>',
      conflictHistory:
        conflictHistory?.import !== undefined
          ? `£${formatCurrency(conflictHistory.import ?? 0)}`
          : '<no data provided>',
      disabled: importValue === undefined,
    },
    {
      id: 'student',
      label: STUDENT_LABEL,
      value: `£${formatCurrency(studentValue ?? 0)}`,
      conflictHistory: `£${formatCurrency(conflictHistory?.student ?? 0)}`,
      editPage: 'personal-info/step-5',
    },
  ];

  let state = determineConflict({
    hasStudentValue,
    hasImportValue,
    studentValue: studentValue ?? '',
    importValue: importValue ?? '',
    conflictHistory,
  });
  if (isRemoved) {
    state = InfoState.Removed;
  }

  return {
    id: 'courseFee',
    index,
    label: 'Listed Tuition Fee',
    subLabel: '(course only)',
    values: {
      import: importValue !== undefined ? `£${formatCurrency(importValue ?? 0)}` : '<no data provided>',
      student: `£${formatCurrency(studentValue ?? 0)}`,
    },
    options,
    state,
  };
}

function courseIsWorkPlacement(student: StudentState, index: number): InfoField {
  const isRemoved = isCourseRemoved(student.offer, index);

  const importOffer = getImportOffer(student.importData, student.offer.intakeId, student.offer.offerId);
  // false and undefined have different meanings for course work placement
  // do not default values to false
  const importValue = importOffer?.courses?.[index]?.isWorkPlacement;
  const studentValue = student.offer.courses?.[index]?.isWorkPlacement;
  const conflictHistory = (student.conflictHistory.courseIsWorkPlacement ?? []).find(c => c.index === index)?.data;
  const hasStudentValue = studentValue !== undefined;
  const resolvedValue = conflictHistory?.resolvedValue;
  const hasResolvedValue = resolvedValue !== undefined;

  const options: InfoOption[] = [
    {
      id: 'import',
      label: IMPORT_LABEL,
      value: importValue !== undefined ? boolToYesNo(importValue) : '<no data provided>',
      conflictHistory:
        conflictHistory?.import !== undefined ? boolToYesNo(conflictHistory.import) : '<no data provided>',
      disabled: importValue === undefined,
    },
    {
      id: 'student',
      label: STUDENT_LABEL,
      value: boolToYesNo(studentValue),
      conflictHistory: boolToYesNo(conflictHistory?.student),
      editPage: 'personal-info/step-5',
    },
  ];

  let state = InfoState.NotFilled;
  if (isRemoved) {
    state = InfoState.Removed;
  } else if (hasStudentValue) {
    if (hasStudentValue && importValue === studentValue) {
      // student has chosen to approve import filled value
      state = InfoState.Approved;
    }

    if (hasStudentValue && hasResolvedValue && studentValue === resolvedValue) {
      // user has chosen to resolve conflict with import, student or passport value
      state = InfoState.Resolved;
    }

    // does the student have a conflict?
    const hasConflictHistory = !!conflictHistory;
    const isResolvedValueEmpty = resolvedValue === undefined;
    const isStudentValueResolved = studentValue === resolvedValue;
    const hasImportValueBeenUpdated = conflictHistory?.import !== importValue;
    const hasImportValueBeenSet = importValue === studentValue;

    if (hasConflictHistory) {
      // has conflict history
      if (isResolvedValueEmpty || !isStudentValueResolved || hasImportValueBeenUpdated) {
        // the conflict has not been resolved yet OR
        // the import or passport value has been updated since the conlict was raised
        state = InfoState.Conflict;
      }
    } else if (hasStudentValue) {
      // there's no conflict history and the value has been set in the student record
      if (!hasImportValueBeenSet) {
        // the value in the student record doesn't match the value in import or passport
        state = InfoState.Conflict;
      }
    }
  } else {
    state = InfoState.NotFilledImport;
  }

  return {
    id: 'courseIsWorkPlacement',
    index,
    label: 'Work Placement',
    values: {
      import: importValue !== undefined ? boolToYesNo(importValue) : '<no data provided>',
      student: boolToYesNo(studentValue),
    },
    options,
    state,
  };
}

function courseLastAcceptanceDate(student: StudentState, index: number): InfoField {
  const isRemoved = isCourseRemoved(student.offer, index);

  const importOffer = getImportOffer(student.importData, student.offer.intakeId, student.offer.offerId);
  const importValue = importOffer?.courses?.[index]?.lastAcceptanceDate ?? '';
  const studentValue = student.offer.courses?.[index]?.lastAcceptanceDate ?? '';
  const hasStudentValue = !!studentValue;
  const options: InfoOption[] = [
    {
      id: 'import',
      label: IMPORT_LABEL,
      value: importValue || '<no data provided>',
      disabled: !importValue,
    },
    {
      id: 'student',
      label: STUDENT_LABEL,
      value: studentValue,
    },
  ];

  // See CS-2167. This field is always imported and not filled out by student.
  let state;
  if (hasStudentValue) {
    state = InfoState.Approved;
  } else {
    state = InfoState.NotFilledImport;
  }

  if (isRemoved) {
    state = InfoState.Removed;
  }

  return {
    id: 'courseLastAcceptanceDate',
    index,
    label: 'Last Acceptance Date',
    values: {
      import: getFormatDateFromObject(getDateFromString(importValue)) || '<no data provided>',
      student: getFormatDateFromObject(getDateFromString(studentValue)),
    },
    options,
    state,
  };
}

function courseOfferEvidence(student: StudentState, index: number): InfoField {
  const isRemoved = isCourseRemoved(student.offer, index);
  const studentQuestionActive =
    !student.config.Checklist.PersonalInfo.Page5_1.hidden && !!student.config.evidenceForOfferInCourseDetails;
  const studentPageComplete = !!student.offer.isPIPage5_1Complete;
  const studentAcknowledged = studentQuestionActive ? studentPageComplete : true;
  const editPageAction = studentQuestionActive ? 'personal-info/step-5-1' : 'edit-offer-evidence-modal';
  const createPageAction = 'edit-offer-evidence-modal';

  const conflictHistory = (student.conflictHistory.courseOfferEvidence ?? []).find(c => c.index === index)?.data;
  const importOffer = getImportOffer(student.importData, student.offer.intakeId, student.offer.offerId);
  const importValue = importOffer?.courses?.[index]?.offerEvidence ?? '';
  const studentValue = student.offer.courses?.[index]?.offerEvidence ?? '';
  const manualValue = conflictHistory?.manual ?? '';
  const hasStudentValue = !!studentValue && studentAcknowledged;
  const hasImportValue = !!importValue;
  const hasManualValue = !!manualValue;
  // Field can be edited by admin.
  const isSetCustom = conflictHistory?.option === 'custom';
  const hasNoValue = !hasStudentValue && !hasImportValue && !hasManualValue;

  let state = determineConflict({
    hasStudentValue,
    hasImportValue,
    studentValue,
    importValue,
    conflictHistory,
  });

  const isSetCustomBeforeStudent = isSetCustom && !hasStudentValue;
  // special rules for when manual edit happened before student filled the page.
  if (isSetCustomBeforeStudent) {
    state = InfoState.Manual;
  } else {
    // Field can be edited by admin, in which case a conflict is resolved, but status should be shown as Approved.
    if (state === InfoState.Resolved && isSetCustom) {
      state = InfoState.Approved;
    }
    // Field is optional if it's not filled out.
    if (hasNoValue) {
      state = InfoState.Optional;
    }
  }

  if (isRemoved) {
    state = InfoState.Removed;
  }

  const sluggedResolvedValue = getProcessedValue(conflictHistory?.resolvedValue);
  const sluggedStudentValue = getProcessedValue(studentValue);
  const hasStudentValueChanged =
    state === InfoState.Conflict && hasStudentValue && conflictHistory && sluggedResolvedValue !== sluggedStudentValue;
  const manualOptionCondition =
    state === InfoState.Conflict ? hasStudentValueChanged && hasManualValue : hasManualValue;
  const options: InfoOption[] = [
    manualOptionCondition
      ? {
          id: 'manual',
          label: MANUAL_LABEL,
          value: manualValue,
          conflictHistory: conflictHistory?.manual,
        }
      : {
          id: 'import',
          label: IMPORT_LABEL,
          value: importValue,
          conflictHistory: conflictHistory?.import,
          disabled: !importValue,
        },
    {
      id: 'student',
      label: STUDENT_LABEL,
      value: studentValue,
      conflictHistory: conflictHistory?.student,
      editPage: editPageAction,
    },
  ];

  const showBigEditButton = !hasNoValue && state !== InfoState.Conflict && state !== InfoState.Removed;
  return {
    id: 'courseOfferEvidence',
    index,
    label: 'Evidence Used for Offer',
    values: {
      import: isSetCustomBeforeStudent ? manualValue : importValue,
      student: studentValue,
    },
    options,
    state,
    ...(showBigEditButton ? { editPage: editPageAction } : {}),
    ...(hasNoValue ? { addFieldBtn: { label: 'Create text', action: createPageAction } } : {}),
    multiLine: true,
  };
}

function courseConditions(course: IImportCourse, index: number): InfoField {
  const text = [
    course?.conditionText1,
    course?.conditionText2,
    course?.conditionText3,
    course?.conditionText4,
    course?.conditionText5,
    course?.conditionText6,
    course?.conditionText7,
    course?.conditionText8,
    course?.conditionText9,
  ]
    .filter(Boolean)
    .join('\n');

  return {
    label: 'Outstanding Conditions',
    id: 'courseConditionsInCourseDetails',
    index,
    values: {
      student: text, // optional field gets mapped to values.student
    },
    state: InfoState.Optional, // optional field doesn't have an image
    options: [],
    multiLine: true,
  };
}

function courseOfferStatus(student: StudentState, index: number): InfoField {
  const isRemoved = isCourseRemoved(student.offer, index);

  const calculateStatus = (): CourseLevelOfferStatus => {
    const importOffer = getImportOffer(student.importData, student.offer.intakeId, student.offer.offerId);
    const course = importOffer?.courses[index];
    if (course?.isDeclined) {
      return CourseLevelOfferStatus.DECLINED;
    }

    if (course?.conditional) {
      return CourseLevelOfferStatus.CONDITIONAL;
    }

    return CourseLevelOfferStatus.UNCONDITIONAL;
  };

  const status = calculateStatus();

  const options: InfoOption[] = [
    {
      id: 'import',
      label: IMPORT_LABEL,
      value: status,
    },
    {
      id: 'student',
      label: STUDENT_LABEL,
      value: status,
      disabled: true,
    },
  ];

  let state = status === CourseLevelOfferStatus.UNCONDITIONAL ? InfoState.Approved : InfoState.Optional;

  if (isRemoved) {
    state = InfoState.Removed;
  }

  return {
    id: 'conditional',
    label: 'Offer Status',
    values: {
      import: status,
      student: status,
    },
    options,
    state,
  };
}

function courseFeeDeposit(student: StudentState): InfoField {
  const importOffer = getImportOffer(student.importData, student.offer.intakeId, student.offer.offerId);
  const importValue = importOffer?.courseFeePaidToDate ?? null;
  const conflictHistory = student.conflictHistory.courseFeeDeposit;
  const hasImportValue = importValue !== null;

  const { courseFeeDepositReqd, courseFeeDeposit } = student.financials;
  const studentAnsweredDeposit = !!courseFeeDepositReqd;
  const studentHasDeposit = courseFeeDepositReqd === 'Yes';

  let studentValue: number | undefined;
  let hasStudentValue: boolean;

  if (studentAnsweredDeposit) {
    studentValue = studentHasDeposit ? (courseFeeDeposit ?? 0) : 0;
    hasStudentValue = true;
  } else {
    studentValue = 0;
    hasStudentValue = false;
  }

  const options: InfoOption[] = [
    {
      id: 'import',
      label: IMPORT_LABEL,
      value: typeof importValue === 'number' ? `£${formatCurrency(importValue)}` : '<no data provided>',
      conflictHistory: `£${formatCurrency(conflictHistory?.import ?? 0)}`,
    },
    {
      id: 'student',
      label: STUDENT_LABEL,
      value: `£${formatCurrency(studentValue ?? 0)}`,
      conflictHistory: `£${formatCurrency(conflictHistory?.student ?? 0)}`,
    },
  ];

  let state = determineConflict({
    hasStudentValue,
    hasImportValue,
    studentValue: studentValue ?? '',
    importValue: importValue ?? '',
    conflictHistory,
  });

  if (student.config.financeSectionStatus === SectionStatusType.INACTIVE) {
    state = InfoState.Skipped;
  }

  return {
    id: 'courseFeeDeposit',
    label: 'Tuition Deposit Received',
    values: {
      import: typeof importValue === 'number' ? `£${formatCurrency(importValue)}` : '<no data provided>',
      student: `£${formatCurrency(studentValue ?? 0)}`,
    },
    options,
    state,
    editPage: 'finances/step-1',
  };
}

function accomFeeDeposit(student: StudentState, config: StudentState['config']): InfoField {
  const accomDepositDisabled = config?.Checklist?.Finances?.Page1?.AccommodationDepositPaid?.visible === false;

  const importOffer = getImportOffer(student.importData, student.offer.intakeId, student.offer.offerId);
  const importValue = accomDepositDisabled ? 0 : (importOffer?.accomFeePaidToDate ?? null);
  const conflictHistory = student.conflictHistory.accomFeeDeposit;
  const hasImportValue = importValue !== null;

  const { accomDepositReqd, accomDeposit } = student.financials;
  const studentAnsweredDeposit = accomDepositDisabled || !!accomDepositReqd;
  const studentHasDeposit = !accomDepositDisabled && accomDepositReqd === 'Yes';

  let studentValue: number | undefined;
  let hasStudentValue: boolean;

  if (studentAnsweredDeposit) {
    studentValue = studentHasDeposit ? (accomDeposit ?? 0) : 0;
    hasStudentValue = true;
  } else {
    studentValue = 0;
    hasStudentValue = false;
  }

  const options: InfoOption[] = [
    {
      id: 'import',
      label: IMPORT_LABEL,
      value: typeof importValue === 'number' ? `£${formatCurrency(importValue)}` : '<no data provided>',
      conflictHistory: `£${formatCurrency(conflictHistory?.import ?? 0)}`,
    },
    {
      id: 'student',
      label: STUDENT_LABEL,
      value: `£${formatCurrency(studentValue ?? 0)}`,
      conflictHistory: `£${formatCurrency(conflictHistory?.student ?? 0)}`,
    },
  ];

  let state = determineConflict({
    hasStudentValue,
    hasImportValue,
    studentValue: studentValue ?? '',
    importValue: importValue ?? '',
    conflictHistory,
  });

  if (student.config.financeSectionStatus === SectionStatusType.INACTIVE) {
    state = InfoState.Skipped;
  }

  return {
    id: 'accomFeeDeposit',
    label: 'Accommodation Deposit Received',
    values: {
      import: typeof importValue === 'number' ? `£${formatCurrency(importValue)}` : '<no data provided>',
      student: `£${formatCurrency(studentValue ?? 0)}`,
    },
    options,
    state,
    editPage: 'finances/step-1',
  };
}

function transferMatePayments(student: StudentState): InfoField {
  const { payments } = student.financials;
  const filteredPayments = payments.filter(
    payment => payment.status === TransactionStatus.Paid || payment.status === TransactionStatus.Pending,
  );
  const state = InfoState.TransferMate;
  let paidAmount = 0;
  let pendingAmount = 0;

  for (const payment of filteredPayments) {
    if (payment.status === TransactionStatus.Paid) {
      paidAmount += payment.amount;
    } else {
      pendingAmount += payment.amount;
    }
  }

  const options: InfoOption[] = filteredPayments.map(payment => ({
    id: payment.status,
    label: `${payment.status === TransactionStatus.Pending ? 'Pending ' : ''}Payment ${formatDate(
      parseDate(payment.initiatedDate),
      'DD MMM YYYY HH:mm',
    )}`,
    value: `£${formatCurrency(payment.amount ?? 0)}`,
  }));

  return {
    id: 'transferMatePayments',
    label: 'TransferMate Payments',
    values: {
      student: `£${formatCurrency(paidAmount ?? 0)} paid ${
        pendingAmount ? `(+ £${formatCurrency(pendingAmount)} pending)` : ''
      }`,
    },
    options,
    state,
  };
}

/* Course can be considered removed
 * if it does not have a field that is always required
 * (Course Name) */
function isCourseRemoved(offer: IOffer, index: number): boolean {
  const isPageComplete = !!offer.isPIPage5Complete;
  const courseName = offer.courses?.[index]?.courseName ?? '';
  return !courseName && isPageComplete;
}

// --- Agent Detail Fields ---

const AGENT_LABEL = 'Updated Value';

export function formatAgentCompanyName(displayName: string, importAgentName?: string | null): string {
  if (importAgentName && importAgentName !== displayName) {
    return `${displayName} (${importAgentName})`;
  }
  return displayName;
}

// Returns true when the import agent details belong to the currently assigned agent.
// Legacy records without importAgentId default to true (show import details, backwards compatible).
function importAgentMatchesAssigned(offer: IOffer): boolean {
  if (offer.importAgentId === undefined) return true;
  return offer.importAgentId === (offer.assignedAgents?.[0]?.id ?? null);
}

function agentDisplayName(student: StudentState, agentMatches: boolean): InfoField {
  const displayName = student.offer.assignedAgents?.[0]?.displayName ?? '';
  const importAgentName = agentMatches ? student.offer.importAgentName : undefined;
  const formattedName = formatAgentCompanyName(displayName, importAgentName);
  const conflictEntry = student.conflictHistory?.agentDisplayName as AgentDisplayNameConflictData | undefined;

  let state: InfoState;
  if (conflictEntry && !conflictEntry.resolvedValue) {
    state = InfoState.Conflict;
  } else if (conflictEntry?.resolvedValue) {
    state = InfoState.Resolved;
  } else if (student.offer.isAgentConfirmed) {
    state = InfoState.Approved;
  } else {
    state = InfoState.NotFilledImport;
  }

  const options: InfoOption[] = [
    {
      id: 'import',
      label: 'University Record',
      value: formattedName,
      conflictHistory: conflictEntry?.import,
    },
  ];

  if (conflictEntry) {
    options.push({
      id: 'student',
      label: 'Student Response',
      value: conflictEntry.student === null ? 'Rejected' : (conflictEntry.student ?? ''),
      disabled: true,
    });
  }

  return {
    id: 'agentDisplayName',
    label: 'Agent Company Name',
    values: { import: formattedName, student: formattedName },
    options,
    state,
    editPage: 'agent-details/step-1',
  };
}

const AGENT_DETAIL_FIELDS: Array<{ id: AgentDetailFieldName; label: string }> = [
  { id: 'agentFamilyName', label: AGENT_DETAIL_FIELD_LABELS.agentFamilyName },
  { id: 'agentGivenName', label: AGENT_DETAIL_FIELD_LABELS.agentGivenName },
  { id: 'branchAddressLine1', label: 'Branch Address Line 1' },
  { id: 'branchAddressLine2', label: 'Branch Address Line 2' },
  { id: 'branchAddressLine3', label: 'Branch Address Line 3' },
  { id: 'branchCity', label: 'Branch City' },
  { id: 'branchCountry', label: 'Branch Country' },
  { id: 'branchPostcode', label: 'Branch Postcode' },
];

function agentDetailField(
  student: StudentState,
  importOffer: IImportOffer | null,
  fieldName: AgentDetailFieldName,
  label: string,
): InfoField {
  const importValue = String(importOffer?.[fieldName] ?? '');
  const offerValue = String(student.offer[fieldName] ?? '');
  const conflictHistory = student.conflictHistory?.[fieldName] as ConflictData<string> | undefined;
  const hasImportValue = !!importValue;
  const hasOfferValue = !!offerValue;
  const isPageComplete = !!student.offer.isAgentDetailsConfirmed;

  const options: InfoOption[] = [
    {
      id: 'import',
      label: IMPORT_LABEL,
      value: importValue,
      conflictHistory: conflictHistory?.import,
    },
    {
      id: 'agent',
      label: AGENT_LABEL,
      value: offerValue,
      conflictHistory: conflictHistory?.agent,
    },
  ];

  let state: InfoState;
  if (conflictHistory?.resolvedValue !== undefined) {
    state = InfoState.Resolved;
  } else if (conflictHistory?.import !== undefined || conflictHistory?.agent !== undefined) {
    state = InfoState.Conflict;
  } else if (isPageComplete) {
    state = InfoState.Approved;
  } else if (hasOfferValue || hasImportValue) {
    state = InfoState.NotFilledImport;
  } else {
    state = InfoState.NotFilled;
  }

  return {
    id: fieldName,
    label,
    values: {
      import: importValue,
      student: offerValue,
    },
    options,
    state,
    editPage: 'agent-details/step-1',
  };
}

export function all(
  student: StudentState,
  config: StudentState['config'],
  uniConfig: ISystemAndUniConfig,
): {
  personalInfo: InfoField[];
  courses: InfoField[][];
  deposit: InfoField[];
  agentDetails: InfoField[];
} {
  const personalInfo: InfoField[] = [];
  const courses: InfoField[][] = [];
  const deposit: InfoField[] = [];
  const agentDetails: InfoField[] = [];

  personalInfo.push(studentNo(student));
  if (student.core.pathway) {
    personalInfo.push(uniPartnerId(student));
  }
  personalInfo.push(gender(student));
  personalInfo.push(familyName(student));
  // display middle name if it is visible in journey, and has imported value (or had conflict resolved)
  if (
    config?.Checklist?.PersonalInfo?.Page1?.MiddleName?.visible &&
    (!!student.importData.otherName || student.conflictHistory.middleName)
  ) {
    personalInfo.push(middleName(student));
  }
  personalInfo.push(givenName(student));
  personalInfo.push(email(student));
  personalInfo.push(dob(student));
  personalInfo.push(nationality(student));
  personalInfo.push(birthCountry(student));
  personalInfo.push(passportNumber(student));
  const renewDocs = getVisible({ documentsHash: student.documents.passport.renew ?? {} });
  if (renewDocs.length > 0) {
    personalInfo.push(passportNumberRenew(student));
  }

  const isPathway = uniConfig.pathwayIds?.[0]?.value ?? false;
  const importOffer = getImportOffer(student.importData, student.offer.intakeId, student.offer.offerId);

  const importCourses = importOffer?.courses ?? [];
  for (const [index] of importCourses.entries()) {
    const fieldSet: InfoField[] = [];
    fieldSet.push(courseName(student, index));

    if (config?.Checklist?.PersonalInfo?.Page5?.StartDate?.visible === true)
      fieldSet.push(courseStartDate(student, index));

    if (config?.Checklist?.PersonalInfo?.Page5?.EndDate?.visible === true) fieldSet.push(courseEndDate(student, index));
    fieldSet.push(courseFee(student, index));

    fieldSet.push(...courseScholarships(student, index));

    fieldSet.push(...courseFeeAfterScholarship(student, index));

    if (config?.Checklist?.PersonalInfo?.Page5?.IsWorkPlacement?.visible === true)
      fieldSet.push(courseIsWorkPlacement(student, index));

    const course = importOffer?.courses?.[index];
    if (course?.lastAcceptanceDate) fieldSet.push(courseLastAcceptanceDate(student, index));

    // For Uni & Pathway - show question if flag in config ("evidenceForOfferInCourseDetails") is true
    // if Uni, and pathway student, check another flag is true too "pathwayStudentsEvidenceForOfferInCourseDetails"
    if (
      (isPathway && uniConfig.evidenceForOfferInCourseDetails) ||
      (!isPathway && !!student.core.pathway && uniConfig.pathwayStudentsEvidenceForOfferInCourseDetails) ||
      (!isPathway && student.core.pathway === undefined && uniConfig.evidenceForOfferInCourseDetails)
    ) {
      fieldSet.push(courseOfferEvidence(student, index));
    }

    if (uniConfig.conditionStatusInCourseDetails) fieldSet.push(courseOfferStatus(student, index));
    if (
      uniConfig.courseConditionsInCourseDetails &&
      course &&
      [
        course?.conditionText1,
        course?.conditionText2,
        course?.conditionText3,
        course?.conditionText4,
        course?.conditionText5,
        course?.conditionText6,
        course?.conditionText7,
        course?.conditionText8,
        course?.conditionText9,
      ].filter(Boolean).length > 0
    ) {
      fieldSet.push(courseConditions(course, index));
    }
    courses.push(fieldSet);
  }

  deposit.push(courseFeeDeposit(student));
  deposit.push(accomFeeDeposit(student, config));
  if (student.financials.payments?.length) {
    deposit.push(transferMatePayments(student));
  }

  const agentMatches = importAgentMatchesAssigned(student.offer);
  const effectiveImportOffer = agentMatches ? importOffer : null;
  const agentDetailsEnabled = config?.agentDetailsEnabled !== false;
  const intakeAfterCutoff = isIntakeAfterAgentDetailsCutoff(student.offer.intakeId, student.summary?.uni ?? '');

  if (student.offer.withAgent && agentDetailsEnabled && intakeAfterCutoff) {
    agentDetails.push(agentDisplayName(student, agentMatches));
    for (const { id, label } of AGENT_DETAIL_FIELDS) {
      agentDetails.push(agentDetailField(student, effectiveImportOffer, id, label));
    }
  }

  return {
    personalInfo,
    courses,
    deposit,
    agentDetails,
  };
}
